import { useEffect, useRef, useState } from "react";
import { Play, Pause, SkipBack, SkipForward, Video as VideoIcon, Headphones } from "lucide-react";
import { api } from "@/lib/api";
import { mediaUrl, extractYouTubeId } from "@/lib/media";
import { Section } from "@/components/Section";

export default function Podcasts() {
  const [items, setItems] = useState([]);
  const [current, setCurrent] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    api.get("/podcasts").then((r) => {
      setItems(r.data);
      if (r.data[0]) setCurrent(r.data[0]);
    }).catch(() => {});
  }, []);

  const isAudio = current && (current.media_type === "audio" || (!current.media_type && current.audio_url));
  const isVideoFile = current && current.media_type === "video_file";
  const isYouTube = current && current.media_type === "video_youtube";

  useEffect(() => {
    if (!isAudio) return;
    const a = audioRef.current; if (!a) return;
    const onTime = () => setTime(a.currentTime);
    const onDur = () => setDuration(a.duration);
    const onEnd = () => setPlaying(false);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onDur);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onDur);
      a.removeEventListener("ended", onEnd);
    };
  }, [current, isAudio]);

  const toggle = () => {
    const a = audioRef.current; if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { a.play(); setPlaying(true); }
  };

  const seek = (e) => {
    const a = audioRef.current; if (!a || !duration) return;
    a.currentTime = (e.target.value / 100) * duration;
  };

  const fmt = (s) => {
    if (!s || isNaN(s)) return "0:00";
    const m = Math.floor(s / 60); const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const selectEpisode = (p) => {
    setCurrent(p);
    setPlaying(false);
    setTime(0);
  };

  const MediaBadge = ({ p }) => {
    const t = p.media_type || (p.audio_url ? "audio" : "audio");
    const isVid = t === "video_file" || t === "video_youtube";
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase font-bold tracking-widest ${isVid ? "bg-[#E65C00] text-white" : "bg-[#FFCC00] text-[#1A1A1A]"}`}>
        {isVid ? <VideoIcon className="w-3 h-3" /> : <Headphones className="w-3 h-3" />} {isVid ? "VIDEO" : "AUDIO"}
      </span>
    );
  };

  return (
    <>
      <section className="bg-[#1A1A1A] text-white py-20 noise-overlay">
        <Section>
          <div className="label-eyebrow mb-4">Podcasts</div>
          <h1 className="heading-xl text-4xl sm:text-5xl lg:text-6xl">TARA <span className="text-[#E65C00]">RADIO</span></h1>
          <p className="mt-4 text-gray-400 max-w-xl">Deep conversations on construction equipment, finance and entrepreneurship — now in audio and video.</p>
        </Section>
      </section>

      <Section className="py-12 grid lg:grid-cols-[1fr_440px] gap-10">
        {/* Episode List */}
        <div className="space-y-3" data-testid="podcast-list">
          {items.map((p) => (
            <button
              key={p.id}
              onClick={() => selectEpisode(p)}
              className={`w-full flex gap-4 p-4 text-left border-2 transition-colors ${current?.id === p.id ? "border-[#E65C00] bg-[#E65C00]/5" : "border-gray-200 hover:border-[#1A1A1A]"}`}
              data-testid={`podcast-ep-${p.id}`}
            >
              <img src={mediaUrl(p.cover_image)} alt={p.title} className="w-24 h-24 object-cover shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-[10px] uppercase tracking-widest text-[#E65C00] font-bold">EP {p.episode_number} {p.duration && `· ${p.duration}`}</div>
                  <MediaBadge p={p} />
                </div>
                <h3 className="font-heading font-bold mt-1 line-clamp-1">{p.title}</h3>
                <p className="text-sm text-gray-600 mt-1 line-clamp-2">{p.description}</p>
              </div>
            </button>
          ))}
          {items.length === 0 && <div className="text-center py-20 border border-dashed border-gray-300 text-gray-500">No episodes yet.</div>}
        </div>

        {/* Player */}
        <aside className="lg:sticky lg:top-28 h-fit bg-[#1A1A1A] text-white" data-testid="podcast-player">
          {current ? (
            <>
              {/* Media area */}
              {isYouTube && extractYouTubeId(current.video_url) ? (
                <div className="aspect-video bg-black">
                  <iframe
                    src={`https://www.youtube.com/embed/${extractYouTubeId(current.video_url)}`}
                    title={current.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                    data-testid="podcast-youtube-iframe"
                  />
                </div>
              ) : isVideoFile ? (
                <video
                  src={mediaUrl(current.video_url)}
                  controls
                  poster={mediaUrl(current.cover_image)}
                  className="w-full aspect-video bg-black"
                  data-testid="podcast-video-player"
                >
                  Your browser does not support video playback.
                </video>
              ) : (
                <div className="aspect-square overflow-hidden">
                  <img src={mediaUrl(current.cover_image)} alt={current.title} className="w-full h-full object-cover" />
                </div>
              )}

              <div className="p-6">
                <div className="flex items-center gap-2">
                  <div className="text-[10px] uppercase tracking-widest text-[#FFCC00]">EPISODE {current.episode_number}</div>
                  <MediaBadge p={current} />
                </div>
                <h3 className="font-heading font-black text-xl mt-2 leading-tight">{current.title}</h3>
                <p className="text-sm text-gray-400 mt-2 leading-relaxed line-clamp-3">{current.description}</p>

                {isAudio && current.audio_url && (
                  <>
                    <audio ref={audioRef} src={mediaUrl(current.audio_url)} data-testid="podcast-audio-element" />
                    <div className="mt-6 space-y-3">
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={duration ? (time / duration) * 100 : 0}
                        onChange={seek}
                        className="w-full accent-[#E65C00]"
                        data-testid="podcast-seek"
                      />
                      <div className="flex justify-between text-xs text-gray-400">
                        <span>{fmt(time)}</span><span>{fmt(duration)}</span>
                      </div>
                      <div className="flex justify-center items-center gap-4 mt-2">
                        <button onClick={() => audioRef.current && (audioRef.current.currentTime -= 15)} className="p-2 hover:text-[#FFCC00]" aria-label="Back 15s"><SkipBack className="w-5 h-5" /></button>
                        <button onClick={toggle} className="bg-[#E65C00] hover:bg-[#CC5200] w-14 h-14 flex items-center justify-center" aria-label={playing ? "Pause" : "Play"} data-testid="podcast-playpause">
                          {playing ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-0.5" />}
                        </button>
                        <button onClick={() => audioRef.current && (audioRef.current.currentTime += 15)} className="p-2 hover:text-[#FFCC00]" aria-label="Forward 15s"><SkipForward className="w-5 h-5" /></button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </>
          ) : (
            <div className="p-10 text-center text-gray-400">Select an episode to play</div>
          )}
        </aside>
      </Section>
    </>
  );
}
